import { useState } from "react";
import { useAccidentalSubmitGuard } from "../hooks/useAccidentalSubmitGuard";
import { getNamespaceTranslations } from "../lib/questionnaire-translations";
import {
  type GenderAudience,
  canCustomerPurchaseProduct,
  customerTagsToGenderRole,
} from "../lib/gender-purchase-eligibility";
import { Button } from "./ui/button";
import { updateShopifyHeader } from "../lib/shopify-header-utils";
import { getStorefrontConfig, getStorefrontUrl } from "../lib/storefront-config";
import { normalizeInput } from "../lib/normalize-input";
import { focusFirstInvalidField } from "../lib/focus-first-invalid";
import { Eye, EyeOff } from "lucide-react";

interface AccountLoginProps {
  email: string;
  onBack: () => void;
  onContinue: (data?: any) => void;
  language?: "en" | "es";
  mdiCustomerId?: string | null;
  productTitle?: string;
  productGenderAudience?: GenderAudience;
  tnc?: boolean;
}

export function AccountLogin({
  email,
  onBack,
  onContinue,
  language = "en",
  mdiCustomerId,
  productTitle,
  productGenderAudience = "all",
  tnc,
}: AccountLoginProps) {
  const t = getNamespaceTranslations(language, "account")!;
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPasswordText, setShowPasswordText] = useState(false);
  const { shouldSuppressSubmit } = useAccidentalSubmitGuard(true);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (shouldSuppressSubmit()) return;
    if (!password.trim()) {
      setError(t.passwordRequiredError || t.fieldRequired || "This field is required");
      focusFirstInvalidField(["password"]);
      return;
    }
    setError("");
    setIsLoading(true);

    try {
      const currentHost = window.location.host;
      const { storeFrontAPI } = getStorefrontConfig(currentHost);
      const storeFrontUrl = getStorefrontUrl(currentHost);

      const query = `
        mutation customerAccessTokenCreate($input: CustomerAccessTokenCreateInput!) {
          customerAccessTokenCreate(input: $input) {
            customerAccessToken {
              accessToken
              expiresAt
            }
            userErrors {
              field
              message
            }
          }
        }
      `;

      const variables = {
        input: {
          email,
          password,
        },
      };

      const response = await fetch(storeFrontUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": storeFrontAPI,
        },
        body: JSON.stringify({ query, variables }),
      });

      const result = await response.json();
      const data = result?.data?.customerAccessTokenCreate;

      if (data?.userErrors?.length) {
        setError(t.loginError || "Incorrect password. Please try again.");
        focusFirstInvalidField(["password"]);
        setIsLoading(false);
        return;
      }

      const token = data?.customerAccessToken?.accessToken;

      // Store token in session cookie for persistence across page reloads
      if (token) {
        document.cookie = `mdi_customer_token=${token}; path=/; max-age=86400; SameSite=Strict; Secure`; // 24 hour expiry, HTTPS only
      }

      // Fetch customer data including tags from Storefront API
      if (token) {
        try {
          // Get customer details including tags
          const customerQuery = `
            query {
              customer(customerAccessToken: "${token}") {
                id
                email
                tags
              }
            }
          `;

          const customerResponse = await fetch(storeFrontUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Shopify-Storefront-Access-Token": storeFrontAPI,
            },
            body: JSON.stringify({ query: customerQuery }),
          });

          const customerResult = await customerResponse.json();
          const customer = customerResult?.data?.customer;

          if (customer) {
            // Extract Shopify customer ID
            const shopifyCustomerId = customer.id.split("/").pop() || customer.id;
            const customerTagsForFlow: string[] = Array.isArray(customer.tags) ? customer.tags : [];

            const genderRole = customerTagsToGenderRole(customerTagsForFlow);
            if (!canCustomerPurchaseProduct(genderRole, productGenderAudience)) {
              setError(
                t.genderPurchaseMismatchError ??
                  "This product is not available for your account profile. Please contact support or choose the product that matches your profile."
              );
              setIsLoading(false);
              return;
            }

            // Check if customer has active subscription for this product
            if (customer.tags && productTitle) {
              const customerTags = customer.tags || [];
              const activeTag = `Active_${productTitle.trim()}`;
              const isActiveSubscriber = customerTags.includes(activeTag);

              if (isActiveSubscriber) {
                // Determine the account URL based on current locale
                const accountUrl = language === 'en' ? '/en/account' : '/account';

                // Call Multipass endpoint to generate login URL
                try {
                  const multipassResponse = await fetch('/apps/rx/generate-multipass', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      customerAccessToken: token
                    })
                  });

                  const multipassData = await multipassResponse.json();
                  let redirectUrl = multipassData.multipass_url || multipassData.redirect;
                  if (redirectUrl) {
                    // Append return_to parameter to redirect to the correct locale account page
                    const separator = redirectUrl.includes('?') ? '&' : '?';
                    redirectUrl = `${redirectUrl}${separator}return_to=${encodeURIComponent(accountUrl)}`;
                    window.location.href = redirectUrl;
                    return;
                  }
                } catch {
                  // Multipass call failed - continue to questionnaire
                }
              }
            }

            // If not active subscriber, continue to questionnaire
            handleContinueToQuestionnaire(shopifyCustomerId, token, customerTagsForFlow);
          } else {
            setError(t.loginErrorGeneric || "Login failed. Please try again.");
            setIsLoading(false);
          }
        } catch {
          setError(t.loginErrorGeneric || "Login failed. Please try again.");
          setIsLoading(false);
          return;
        }
      }
    } catch {
      setError(t.loginErrorGeneric || "Login failed. Please try again.");
      setIsLoading(false);
    }
  };

  const handleContinueToQuestionnaire = async (
    shopifyCustomerId: string,
    token: string,
    shopifyCustomerTags: string[] = []
  ) => {
    // Generate multipass URL using the customer access token
    try {
      const multipassResponse = await fetch('/apps/rx/generate-multipass', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerAccessToken: token
        })
      });

      const multipassData = await multipassResponse.json();
      const multipassUrl = multipassData.multipass_url || multipassData.redirect;

      if (!multipassUrl) {
        await continueToQuestionnaire(shopifyCustomerId, token, shopifyCustomerTags);
        return;
      }

      // Create hidden iframe to load multipass URL and set Shopify cookie
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      // Replace myshopify.com with custom domain to avoid Firefox cross-site cookie blocking
      let iframeSrc = multipassUrl;
      if (iframeSrc.includes('srx-meds.myshopify.com')) {
        iframeSrc = iframeSrc.replace('srx-meds.myshopify.com', 'srreceta.com');
      }
      iframe.src = iframeSrc;
      document.body.appendChild(iframe);

      // Track start time for minimum wait
      const startTime = Date.now();
      const minWait = 1500; // Minimum 1.5 seconds to ensure cookie is set

      iframe.onload = async () => {
        // Wait at least minWait milliseconds
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, minWait - elapsed);
        await new Promise(resolve => setTimeout(resolve, remaining));

        // Update the Shopify header to show logged-in state
        await updateShopifyHeader();

        // Continue to questionnaire
        await continueToQuestionnaire(shopifyCustomerId, token, shopifyCustomerTags);

        // Clean up iframe
        document.body.removeChild(iframe);
      };

      iframe.onerror = async () => {
        document.body.removeChild(iframe);
        await continueToQuestionnaire(shopifyCustomerId, token, shopifyCustomerTags);
      };
    } catch {
      await continueToQuestionnaire(shopifyCustomerId, token, shopifyCustomerTags);
    }
  };

  const continueToQuestionnaire = async (
    shopifyCustomerId: string,
    token: string,
    shopifyCustomerTags: string[] = []
  ) => {
    const tagPayload =
      shopifyCustomerTags.length > 0 ? { shopifyCustomerTags: shopifyCustomerTags } : {};
    // If we have MDI customer ID, fetch patient details and continue
    if (mdiCustomerId) {
      try {
        const patientResponse = await fetch(`/apps/rx/customer/${mdiCustomerId}`, {
          method: "GET",
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          }
        });

        const patientData = await patientResponse.json();
        const normalizedFirstName = normalizeInput(patientData?.first_name || "");
        const normalizedLastName = normalizeInput(patientData?.last_name || "");

        const continueData = {
          customer_id: mdiCustomerId, // MDI patient ID
          shopify_customer_id: shopifyCustomerId, // Shopify customer ID
          email: email,
          firstName: normalizedFirstName,
          lastName: normalizedLastName,
          state: patientData?.state || '',
          customerAccessToken: token,
          has_shopify_account: true,
          has_mdi_account: true,
          tnc,
          ...tagPayload,
        };
        onContinue(continueData);
      } catch {
        const continueData = {
          customer_id: mdiCustomerId,
          shopify_customer_id: shopifyCustomerId,
          email: email,
          customerAccessToken: token,
          has_shopify_account: true,
          has_mdi_account: true,
          tnc,
          ...tagPayload,
        };
        onContinue(continueData);
      }
    } else {
      const continueData = {
        shopify_customer_id: shopifyCustomerId,
        email: email,
        customerAccessToken: token,
        // Reaching this branch always means an existing Shopify account was just logged into.
        has_shopify_account: true,
        tnc,
        ...tagPayload,
      };
      onContinue(continueData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col flex-1 mt-8 gap-y-[30px]">
      {/* Content */}
      <div className="flex-1 pb-0">
        {/* Page Title */}
        <h1
          className="text-[24px] sm:!text-[30px] leading-[30px] sm:!leading-[36px] font-normal mb-6"
          style={{ fontFamily: "TT Commons Pro" }}
        >
          {t.returningUserTitle}
        </h1>

        {/* Email display with change option */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-2 mb-6">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0">
            <span
              className="text-[16px] leading-[22px] text-gray-500 shrink-0"
              style={{ fontFamily: "TT Commons Pro" }}
            >
              {t.emailLabel}:
            </span>
            <span
              className="text-[16px] leading-[22px] text-gray-700 break-all"
              style={{ fontFamily: "TT Commons Pro" }}
            >
              {email}
            </span>
          </div>
          <button
            type="button"
            onClick={onBack}
            className="text-[16px] leading-[22px] text-[#2D55FB] hover:underline shrink-0 self-end sm:self-auto"
            style={{ fontFamily: "TT Commons Pro" }}
          >
            {language === "es" ? "Cambiar" : "Change"}
          </button>
        </div>

        {/* Password Field */}
        <div className="space-y-3">
          <input
            type="email"
            name="email"
            autoComplete="username"
            value={email}
            readOnly
            tabIndex={-1}
            aria-hidden="true"
            className="sr-only"
          />
          <label htmlFor="password" className="block text-sm font-medium text-black required-label">
            {t.passwordLabel}
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPasswordText ? "text" : "password"}
              name="password"
              autoComplete="current-password"
              className={`w-full !border-0 ring-1 ring-inset rounded-lg px-4 !py-[10.5px] leading-6 text-[16px] bg-white pr-12 ${
                error ? "ring-red-500" : "ring-gray-300"
              }`}
              placeholder={t.passwordPlaceholder}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (error) setError(""); // Clear error when user starts typing
              }}
              aria-describedby={error ? "password-error" : undefined}
              aria-invalid={!!error}
              aria-required="true"
              data-testid="login_input-password"
            />
            <button
              type="button"
              onClick={() => setShowPasswordText(!showPasswordText)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
              aria-label={showPasswordText ? (language === "es" ? "Ocultar contraseña" : "Hide password") : (language === "es" ? "Mostrar contraseña" : "Show password")}
            >
              {showPasswordText ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
          {error && (
            <p
              id="password-error"
              className="text-red-600 text-sm mt-1"
              style={{ fontFamily: "TT Commons Pro" }}
              role="alert"
              aria-live="assertive"
            >
              {error}
            </p>
          )}

          {/* Forgot Password Link */}
          <div className="text-right">
            <a
              href={language === 'en' ? '/en/account/login#recover' : '/account/login#recover'}
              className="text-sm text-[#2D55FB] hover:underline"
              style={{ fontFamily: "TT Commons Pro" }}
            >
              {t.forgotPassword}
            </a>
          </div>
        </div>
      </div>

      {/* Continue Button — always full colour; validate on submit */}
      <div>
        <Button
          type="submit"
          disabled={isLoading}
          aria-busy={isLoading}
          data-testid="login-continue"
          className={`w-full h-[50px] text-white text-lg leading-6 font-medium rounded-full flex items-center justify-center transition-colors bg-[#2D55FB] ${
            isLoading ? "cursor-not-allowed" : "hover:bg-[#2347E8]"
          }`}
        >
          {isLoading ? (
            <svg
              className="animate-spin h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              role="status"
              aria-label="Loading"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              ></path>
            </svg>
          ) : (
            t.continue
          )}
        </Button>
      </div>
    </form>
  );
}
